import { useState, useEffect, useMemo, useRef } from 'react';
import { Icon } from '@iconify/react';
import JoditEditor from 'jodit-react';
import MemberPicker from './MemberPicker';
import FlatPicker from './FlatPicker';
import {
    FALLBACK_ENUMS,
    AUDIENCE_LABEL,
    humanize,
    toPlainText,
} from './noticeConstants';
import { notify } from '../../../utils/notification';

// Mirrors the upload middleware's limits for notice attachments.
const MAX_NOTICE_FILES = 5;
const MAX_NOTICE_FILE_MB = 10;
const MAX_NOTICE_FILE_BYTES = MAX_NOTICE_FILE_MB * 1024 * 1024;

// Shared create/edit form for notices and circulars.
//
// The parent owns submission; this component only validates and hands back a
// clean payload. Attachments are opt-in (create flow uploads them after the
// record exists, since the id is needed for the upload URL) — in edit mode they
// are managed on the detail view instead.
const NoticeForm = ({
    initial,
    enums,
    wings = [],
    // Society roster for the "Specific members" audience. Loaded by the parent
    // from /api/users/dropdown.
    members = [],
    flatOptions = [],
    membersLoading = false,
    onSubmit,
    onCancel,
    submitLabel = 'Save',
    submitting = false,
    mode = 'create',
    enableAttachments = false,
    existingAttachments = [],
    // Locks the type selector when the caller already fixed the type.
    lockedType = null,
}) => {
    // The notice_code assigned at creation encodes the type (NOT0001 /
    // CIR0001), so the backend refuses to change it afterwards. Reflect that
    // here instead of offering a control whose change would be silently dropped.
    const typeLocked = !!lockedType || mode === 'edit';
    const [files, setFiles] = useState([]);
    const bodyEditorRef = useRef(null);

    const editorConfig = useMemo(() => ({
        readonly: false,
        placeholder: 'Write the full notice text residents will read...',
        height: 280,
        toolbarButtonSize: 'small',
        buttons: [
            'bold', 'italic', 'underline', 'strikethrough', '|',
            'ul', 'ol', '|',
            'font', 'fontsize', 'brush', '|',
            'align', '|',
            'link', 'table', '|',
            'undo', 'redo', '|',
            'eraser', 'source',
        ],
        removeButtons: ['about'],
        showCharsCounter: false,
        showWordsCounter: false,
        showXPathInStatusbar: false,
    }), []);

    const [form, setForm] = useState({
        notice_type: lockedType || 'NOTICE',
        title: '',
        summary: '',
        body: '',
        category: 'GENERAL',
        priority: 'NORMAL',
        audience: 'ALL',
        recipient_user_ids: [],
        flat_ids: [],
        wing_ids: [],
        publish_at: '',
        expires_at: '',
        is_pinned: false,
        requires_acknowledgement: false,
        send_email: false,
        send_sms: false,
        send_push: false,
        ...initial,
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (initial) setForm(f => ({ ...f, ...initial }));
    }, [initial]);

    // Local "YYYY-MM-DDTHH:mm" -> absolute ISO instant.
    const toInstant = (localValue) => {
        if (!localValue) return undefined;
        const dt = new Date(localValue);
        return Number.isNaN(dt.getTime()) ? undefined : dt.toISOString();
    };

    const set = (k, v) => {
        setForm(f => ({ ...f, [k]: v }));
        setErrors(e => ({ ...e, [k]: undefined }));
    };

    const types = enums?.types || FALLBACK_ENUMS.types;
    const categories = enums?.categories || FALLBACK_ENUMS.categories;
    const priorities = enums?.priorities || FALLBACK_ENUMS.priorities;
    const audiences = enums?.audiences || FALLBACK_ENUMS.audiences;

    const toggleWing = (wingId) => {
        setForm(f => {
            const current = Array.isArray(f.wing_ids) ? f.wing_ids : [];
            return {
                ...f,
                wing_ids: current.includes(wingId)
                    ? current.filter(w => w !== wingId)
                    : [...current, wingId],
            };
        });
    };

    // A circular is an issued document: always emailed, always acknowledged and
    // never expiring. The backend enforces this; the form shows it rather than
    // offering choices that would be overridden on save.
    const isCircular = form.notice_type === 'CIRCULAR';

    // `publish` is true only for Save & Publish / Save & Schedule.
    const validate = (publish = false) => {
        const e = {};
        if (!form.title?.trim()) e.title = 'Title is required';
        else if (form.title.trim().length < 3) e.title = 'Title must be at least 3 characters';

        // The editor emits "<p><br></p>" for an empty document, so length is
        // measured on the stripped text rather than the raw HTML.
        if (!toPlainText(form.body)) e.body = 'Notice content is required';
        else if (toPlainText(form.body).length < 10) e.body = 'Content must be at least 10 characters';

        if (!form.notice_type) e.notice_type = 'Type is required';
        if (!form.category) e.category = 'Category is required';
        if (!form.priority) e.priority = 'Priority is required';
        if (!form.audience) e.audience = 'Audience is required';
        if (form.audience === 'CUSTOM' && (form.recipient_user_ids || []).length === 0) {
            e.recipient_user_ids = 'Choose at least one member';
        }
        if (form.audience === 'FLATS' && (form.flat_ids || []).length === 0) {
            e.flat_ids = 'Choose at least one flat';
        }

        if (publish && form.publish_at && new Date(form.publish_at) <= new Date()) {
            e.publish_at = 'Scheduled time must be in the future — leave this empty to publish now';
        }
        if (!isCircular && form.publish_at && form.expires_at && form.expires_at <= form.publish_at) {
            e.expires_at = 'Expiry must be after the publish date';
        }
        // An expiry already in the past would publish something dead on arrival: the
        // notice saves as PUBLISHED, is flipped to EXPIRED on the next read, and never
        // reaches the board. Previously only checked when publish_at was empty, so
        // setting both a schedule and a past expiry slipped through.
        if (!isCircular && form.expires_at && new Date(form.expires_at) <= new Date()) {
            e.expires_at = 'Expiry must be in the future';
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    // `publish` distinguishes "Save as Draft" from "Save & Publish" — the parent
    // decides whether that means POST + publish or a plain status change.
    const handleSubmit = (ev, publish = false) => {
        ev.preventDefault();
        if (!validate(publish)) return;
        const payload = {
            title: form.title.trim(),
            body: form.body,
            category: form.category,
            priority: form.priority,
            audience: form.audience,
            is_pinned: !!form.is_pinned,
            requires_acknowledgement: !!form.requires_acknowledgement,
            send_email: !!form.send_email,
            // Pinned off until those channels actually have a transport, so no
            // record claims a delivery that never happened.
            send_sms: false,
            send_push: false,
        };
        // Type is set once, at creation. The update endpoint rejects the key
        // outright rather than silently ignoring it, so it must not be sent.
        if (mode === 'create') payload.notice_type = form.notice_type;
        // Sent even when blank so clearing the field actually clears the
        // stored summary — omitting the key would leave the old one in place.
        payload.summary = form.summary?.trim() || '';
        if (form.publish_at) payload.publish_at = toInstant(form.publish_at);
        if (form.expires_at) payload.expires_at = toInstant(form.expires_at);
        // A named list and wing targeting are alternatives, not layers — the
        // members were chosen individually, so a wing filter on top would only
        // silently drop some of them.
        if (form.audience === 'CUSTOM') {
            payload.recipient_user_ids = (form.recipient_user_ids || []).map(Number);
        } else if (form.audience === 'FLATS') {
            payload.flat_ids = (form.flat_ids || []).map(Number);
        } else if (Array.isArray(form.wing_ids) && form.wing_ids.length > 0) {
            payload.wing_ids = form.wing_ids.map(Number);
        }
        onSubmit(payload, enableAttachments ? files : [], publish);
    };

    // Picking files a second time used to REPLACE the first selection, so
    // attaching them one at a time left only the last - "it accepts only one
    // file". Each pick now adds to the list, up to the backend's limit of 5.
    const handleFilesChange = (e) => {
        const picked = Array.from(e.target.files || []);
        e.target.value = '';
        if (picked.length === 0) return;

        const tooBig = picked.filter(f => f.size > MAX_NOTICE_FILE_BYTES);
        if (tooBig.length) notify('error', `Each attachment must be under ${MAX_NOTICE_FILE_MB} MB`);
        const accepted = picked.filter(f => f.size <= MAX_NOTICE_FILE_BYTES);
        if (accepted.length === 0) return;

        setFiles(prev => {
            const seen = new Set(prev.map(f => `${f.name}:${f.size}`));
            const merged = [...prev];
            accepted.forEach(f => {
                const key = `${f.name}:${f.size}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    merged.push(f);
                }
            });
            if (merged.length > MAX_NOTICE_FILES) {
                notify('error', `Too many files. Max ${MAX_NOTICE_FILES} attachments`);
                return merged.slice(0, MAX_NOTICE_FILES);
            }
            return merged;
        });
    };

    const removeFile = (idx) => setFiles(fs => fs.filter((_, i) => i !== idx));

    const inputCls = (field) =>
        `tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-rounded-md focus:tw-outline-none ${errors[field] ? 'tw-border-rose-400 focus:tw-border-rose-500' : 'tw-border-gray-200 focus:tw-border-sky-500'
        }`;

    const labelCls = 'tw-block tw-text-[11px] tw-font-semibold tw-text-gray-700 tw-mb-1';
    const errorCls = 'tw-text-[10px] tw-text-rose-600 tw-mt-1';

    return (
        <form onSubmit={(e) => handleSubmit(e, false)} className="tw-space-y-4">
            {/* Type + title */}
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-4 tw-gap-3">
                <div>
                    <label className={labelCls}>Type <span className="tw-text-rose-500">*</span></label>
                    <select
                        value={form.notice_type}
                        onChange={e => set('notice_type', e.target.value)}
                        disabled={typeLocked}
                        className={`${inputCls('notice_type')} disabled:tw-bg-gray-50 disabled:tw-text-gray-500`}
                    >
                        {types.map(t => <option key={t} value={t}>{humanize(t)}</option>)}
                    </select>
                    {errors.notice_type && <p className={errorCls}>{errors.notice_type}</p>}
                </div>
                <div className="sm:tw-col-span-3">
                    <label className={labelCls}>Title <span className="tw-text-rose-500">*</span></label>
                    <input
                        type="text"
                        value={form.title}
                        onChange={e => set('title', e.target.value)}
                        placeholder="e.g. Water supply interruption on 12 Sept"
                        className={inputCls('title')}
                    />
                    {errors.title && <p className={errorCls}>{errors.title}</p>}
                </div>
            </div>

            {/* Summary — the one-liner shown on cards and in push notifications */}
            <div>
                <label className={labelCls}>
                    Summary
                    <span className="tw-font-normal tw-text-gray-400"> — shown on the notice board card and in push alerts</span>
                </label>
                <input
                    type="text"
                    value={form.summary || ''}
                    onChange={e => set('summary', e.target.value)}
                    maxLength={200}
                    placeholder="One line residents will see before opening the notice"
                    className={inputCls('summary')}
                />
                <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1">{(form.summary || '').length}/200</p>
            </div>

            {/* Body */}
            <div>
                <label className={labelCls}>Content <span className="tw-text-rose-500">*</span></label>
                <div className={errors.body ? 'tw-rounded-md tw-ring-1 tw-ring-rose-400' : ''}>
                    <JoditEditor
                        ref={bodyEditorRef}
                        value={form.body || ''}
                        config={editorConfig}
                        onBlur={(newContent) => set('body', newContent)}
                    />
                </div>
                {errors.body && <p className={errorCls}>{errors.body}</p>}
            </div>

            {/* Classification */}
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                <div>
                    <label className={labelCls}>Category <span className="tw-text-rose-500">*</span></label>
                    <select value={form.category} onChange={e => set('category', e.target.value)} className={inputCls('category')}>
                        {categories.map(c => <option key={c} value={c}>{humanize(c)}</option>)}
                    </select>
                    {errors.category && <p className={errorCls}>{errors.category}</p>}
                </div>
                <div>
                    <label className={labelCls}>Priority <span className="tw-text-rose-500">*</span></label>
                    <select value={form.priority} onChange={e => set('priority', e.target.value)} className={inputCls('priority')}>
                        {priorities.map(p => <option key={p} value={p}>{humanize(p)}</option>)}
                    </select>
                    {errors.priority && <p className={errorCls}>{errors.priority}</p>}
                </div>
                <div>
                    <label className={labelCls}>Audience <span className="tw-text-rose-500">*</span></label>
                    <select value={form.audience} onChange={e => set('audience', e.target.value)} className={inputCls('audience')}>
                        {audiences.map(a => <option key={a} value={a}>{AUDIENCE_LABEL[a] || humanize(a)}</option>)}
                    </select>
                    {errors.audience && <p className={errorCls}>{errors.audience}</p>}
                </div>
            </div>

            {/* Named recipients — the audience IS this list */}
            {form.audience === 'CUSTOM' && (
                <div>
                    <label className={labelCls}>
                        Choose members <span className="tw-text-rose-500">*</span>
                        <span className="tw-font-normal tw-text-gray-400"> — only these members see the notice and receive the notifications below</span>
                    </label>
                    <MemberPicker
                        members={members}
                        loading={membersLoading}
                        selected={form.recipient_user_ids || []}
                        onChange={(ids) => set('recipient_user_ids', ids)}
                        error={errors.recipient_user_ids}
                    />
                    {errors.recipient_user_ids && <p className={errorCls}>{errors.recipient_user_ids}</p>}
                </div>
            )}

            {/* Named flats — everyone attached to the unit is reached */}
            {form.audience === 'FLATS' && (
                <div>
                    <label className={labelCls}>
                        Choose flats <span className="tw-text-rose-500">*</span>
                        <span className="tw-font-normal tw-text-gray-400"> — reaches the owners and tenants of each selected unit</span>
                    </label>
                    <FlatPicker
                        flats={flatOptions}
                        loading={membersLoading}
                        selected={form.flat_ids || []}
                        onChange={(ids) => set('flat_ids', ids)}
                        error={errors.flat_ids}
                    />
                    {errors.flat_ids && <p className={errorCls}>{errors.flat_ids}</p>}
                </div>
            )}

            {/* Wing targeting — optional narrowing on top of a role audience */}
            {form.audience !== 'CUSTOM' && form.audience !== 'FLATS' && wings.length > 0 && (
                <div>
                    <label className={labelCls}>
                        Limit to wings
                        <span className="tw-font-normal tw-text-gray-400"> — leave empty to reach the whole society</span>
                    </label>
                    <div className="tw-flex tw-flex-wrap tw-gap-2">
                        {wings.map(w => {
                            const id = w.wing_id;
                            const active = (form.wing_ids || []).includes(id);
                            return (
                                <button
                                    key={id}
                                    type="button"
                                    onClick={() => toggleWing(id)}
                                    className={`tw-px-2.5 tw-py-1 tw-rounded-full tw-text-[11px] tw-font-medium tw-border tw-transition-colors ${active
                                        ? 'tw-bg-sky-600 tw-text-white tw-border-sky-600'
                                        : 'tw-bg-white tw-text-gray-600 tw-border-gray-200 hover:tw-bg-gray-50'
                                        }`}
                                >
                                    {w.title || w.number || `Wing ${id}`}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Scheduling */}
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
                <div>
                    <label className={labelCls}>
                        Publish at
                        <span className="tw-font-normal tw-text-gray-400"> — leave empty to publish immediately</span>
                    </label>
                    <input
                        type="datetime-local"
                        value={form.publish_at || ''}
                        onChange={e => set('publish_at', e.target.value)}
                        className={inputCls('publish_at')}
                    />
                    {errors.publish_at && <p className={errorCls}>{errors.publish_at}</p>}
                </div>
                <div>
                    <label className={labelCls}>
                        Expires at
                        <span className="tw-font-normal tw-text-gray-400">
                            {isCircular ? ' — circulars stay on the board' : ' — drops off the board after this'}
                        </span>
                    </label>
                    <input
                        type="datetime-local"
                        value={isCircular ? '' : (form.expires_at || '')}
                        onChange={e => set('expires_at', e.target.value)}
                        disabled={isCircular}
                        className={`${inputCls('expires_at')} disabled:tw-bg-gray-50 disabled:tw-text-gray-400`}
                    />
                    {isCircular
                        ? <p className="tw-text-[10px] tw-text-gray-500 tw-mt-1">A circular is a permanent record — it does not expire.</p>
                        : errors.expires_at && <p className={errorCls}>{errors.expires_at}</p>}
                </div>
            </div>

            {/* Flags */}
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
                <label className="tw-flex tw-items-start tw-gap-2 tw-p-3 tw-border tw-border-gray-200 tw-rounded-md tw-cursor-pointer hover:tw-bg-gray-50">
                    <input
                        type="checkbox"
                        checked={!!form.is_pinned}
                        onChange={e => set('is_pinned', e.target.checked)}
                        className="form-check-input tw-mt-0.5 tw-flex-shrink-0"
                    />
                    <span>
                        <span className="tw-block tw-text-xs tw-font-semibold tw-text-gray-800">Pin to top</span>
                        <span className="tw-block tw-text-[10px] tw-text-gray-500">Keeps this above other notices on the board</span>
                    </span>
                </label>
                <label className="tw-flex tw-items-start tw-gap-2 tw-p-3 tw-border tw-border-gray-200 tw-rounded-md tw-cursor-pointer hover:tw-bg-gray-50">
                    <input
                        type="checkbox"
                        checked={isCircular ? true : !!form.requires_acknowledgement}
                        onChange={e => set('requires_acknowledgement', e.target.checked)}
                        disabled={isCircular}
                        className="form-check-input tw-mt-0.5 tw-flex-shrink-0"
                    />
                    <span>
                        <span className="tw-block tw-text-xs tw-font-semibold tw-text-gray-800">Require acknowledgement</span>
                        <span className="tw-block tw-text-[10px] tw-text-gray-500">
                            {isCircular ? 'Always on for circulars — recipients must confirm they have read it' : 'Residents must confirm they have read it'}
                        </span>
                    </span>
                </label>
            </div>

            {/* Delivery channels.
                Only email has a transport behind it today. SMS and push are
                shown disabled rather than hidden so it's clear they're planned
                but not live — a ticked box that silently sends nothing is worse
                than no box at all. */}
            <div>
                <label className={labelCls}>
                    Notify members by email
                    <span className="tw-font-normal tw-text-gray-400"> — sent when the notice is published</span>
                </label>
                <div className="tw-px-3 tw-py-2.5 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-md tw-space-y-2">
                    <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-700 tw-cursor-pointer">
                        <input
                            type="checkbox"
                            checked={isCircular ? true : !!form.send_email}
                            onChange={e => set('send_email', e.target.checked)}
                            disabled={isCircular}
                            className="form-check-input tw-flex-shrink-0"
                        />
                        <Icon icon="mdi:email-outline" className="tw-text-sm tw-text-gray-500" />
                        {isCircular
                            ? 'Emailed to everyone this circular reaches (always)'
                            : 'Email a copy to everyone this notice reaches'}
                    </label>
                    <div className="tw-flex tw-flex-wrap tw-gap-4 tw-pt-1 tw-border-t tw-border-gray-200">
                        {[
                            { label: 'App notification', icon: 'mdi:bell-outline' },
                            { label: 'SMS', icon: 'mdi:message-text-outline' },
                        ].map(ch => (
                            <span key={ch.label} className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-400">
                                <input type="checkbox" className="form-check-input tw-flex-shrink-0" disabled checked={false} readOnly />
                                <Icon icon={ch.icon} className="tw-text-sm" />
                                {ch.label}
                                <span className="tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[9px] tw-font-semibold tw-bg-gray-200 tw-text-gray-500">
                                    Not available yet
                                </span>
                            </span>
                        ))}
                    </div>
                </div>
                {form.send_email && (
                    <p className="tw-text-[10px] tw-text-gray-500 tw-mt-1 tw-flex tw-items-center tw-gap-1">
                        <Icon icon="mdi:information-outline" />
                        Members without an email address on file simply won&apos;t receive a copy — the notice still appears on their board.
                    </p>
                )}
            </div>

            {enableAttachments && (
                <div>
                    <label className={labelCls}>
                        Attachments
                        <span className="tw-font-normal tw-text-gray-400">
                            {` — up to ${MAX_NOTICE_FILES} files, ${MAX_NOTICE_FILE_MB} MB each`}
                            {files.length > 0 ? ` · ${files.length} selected, pick again to add more` : ''}
                        </span>
                    </label>
                    {existingAttachments.length > 0 && (
                        <p className="tw-text-[11px] tw-text-gray-500 tw-mb-1.5">
                            {existingAttachments.length} file{existingAttachments.length === 1 ? '' : 's'} already attached — anything picked here is added alongside them. Remove files from the notice&apos;s view page.
                        </p>
                    )}
                    <input
                        type="file"
                        multiple
                        onChange={handleFilesChange}
                        className="tw-block tw-w-full tw-text-xs tw-text-gray-600 file:tw-mr-3 file:tw-px-3 file:tw-py-1.5 file:tw-rounded-md file:tw-border-0 file:tw-text-xs file:tw-font-medium file:tw-bg-sky-50 file:tw-text-sky-700 hover:file:tw-bg-sky-100"
                    />
                    {files.length > 0 && (
                        <ul className="tw-mt-2 tw-space-y-1">
                            {files.map((f, i) => (
                                <li key={`${f.name}-${i}`} className="tw-flex tw-items-center tw-justify-between tw-px-2.5 tw-py-1.5 tw-bg-gray-50 tw-rounded tw-text-[11px] tw-text-gray-700">
                                    <span className="tw-flex tw-items-center tw-gap-1.5 tw-truncate">
                                        <Icon icon="mdi:paperclip" className="tw-text-gray-400" />
                                        <span className="tw-truncate">{f.name}</span>
                                    </span>
                                    <button type="button" onClick={() => removeFile(i)} className="tw-text-rose-600 hover:tw-text-rose-700">
                                        <Icon icon="mdi:close" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {/* Actions */}
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2 tw-pt-2 tw-border-t tw-border-gray-100">
                <button
                    type="button"
                    onClick={onCancel}
                    className="tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={submitting}
                    className="tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-700 tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50 disabled:tw-opacity-50"
                >
                    {submitting ? 'Saving...' : (mode === 'create' ? 'Save as Draft (not on board)' : submitLabel)}
                </button>
                <button
                    type="button"
                    onClick={(e) => handleSubmit(e, true)}
                    disabled={submitting}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-white tw-bg-sky-600 hover:tw-bg-sky-700 tw-rounded-md disabled:tw-opacity-50"
                >
                    <Icon icon="mdi:send-outline" className="tw-text-base" />
                    {form.publish_at ? 'Save & Schedule' : 'Save & Publish'}
                </button>
            </div>
        </form>
    );
};

export default NoticeForm;
